import { useState } from 'react';
import { useCatalog, useIssueInvoice, useSendInvoice, useStudioClient, useUpdateDraftInvoice } from '@/api/studio';
import type { Invoice, Project } from '@/api/types';
import { LinesEditor } from '@/components/LinesEditor';
import { Dialog } from '@/components/ui';
import { useStudioTerms } from '@/lib/contour';
import { formatMoney } from '@/lib/format';
import { blankLine, fromInvoiceLine, isoInDays, lineProblem, minorToText, sumOf, toInput, type LineDraft } from '@/lib/lines';

/**
 * An invoice is a list of free lines (title, description, quantity, unit, price); catalog services are quick
 * templates that prefill a line, which stays editable. «Сохранить черновик» keeps it from the client,
 * «Выставить» sends it. With `draft` the dialog edits a draft that is already saved.
 */
export function IssueInvoiceDialog({ clientId, projects, open, onClose, draft }: { clientId: string; projects: Project[]; open: boolean; onClose: () => void; draft?: Invoice }) {
  return (
    <Dialog open={open} onClose={onClose} title={draft ? `Черновик счёта № ${draft.number}` : 'Выставить счёт'} wide>
      <InvoiceForm clientId={clientId} projects={projects} draft={draft} onClose={onClose} />
    </Dialog>
  );
}

function InvoiceForm({ clientId, projects, draft, onClose }: { clientId: string; projects: Project[]; draft?: Invoice; onClose: () => void }) {
  const { data: catalog } = useCatalog();
  const issue = useIssueInvoice();
  const update = useUpdateDraftInvoice();
  const send = useSendInvoice();
  const { currency } = useStudioTerms();
  const [projectId, setProjectId] = useState(draft?.projectId ?? projects[0]?.id ?? '');
  const [lines, setLines] = useState<LineDraft[]>(() => draft?.lines.map(fromInvoiceLine) ?? []);
  const [dueDate, setDueDate] = useState(draft?.dueAt ?? isoInDays(5));
  const [basis, setBasis] = useState(draft?.basis ?? '');
  const [comment, setComment] = useState(draft?.comment ?? '');
  const [shown, setShown] = useState(false);
  // The legal entity billed: the project's payer unless one is picked (a draft keeps its own).
  const payers = useStudioClient(clientId).data?.payers ?? [];
  const [picked, setPicked] = useState<string>();
  const projectPayer = projects.find((p) => p.id === projectId)?.payerId ?? payers.find((x) => x.isDefault)?.id ?? '';
  const payerId = picked ?? projectPayer;
  const total = sumOf(lines, currency);
  const invalid = lines.length === 0 || lines.some((l) => lineProblem(l));
  const pending = issue.isPending || update.isPending || send.isPending;
  const error = (issue.error ?? update.error ?? send.error) as Error | null;

  const body = () => ({
    projectId: projectId || undefined,
    lines: lines.map(toInput),
    dueDate,
    ...(comment.trim() ? { comment: comment.trim() } : {}),
    ...(basis.trim() ? { basis: basis.trim() } : {}),
  });
  /** `sendNow`: false saves a draft the client does not see. */
  const submit = (sendNow: boolean) => {
    setShown(true);
    if (invalid) return;
    if (draft) update.mutate({ id: draft.id, input: body() }, { onSuccess: () => (sendNow ? send.mutate(draft.id, { onSuccess: onClose }) : onClose()) });
    else issue.mutate({ clientId, ...body(), ...(payerId ? { payerId } : {}), draft: !sendNow }, { onSuccess: onClose });
  };

  return (
    <form
      className="space-y-4 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        submit(true);
      }}
    >
      {!!catalog?.length && (
        <fieldset>
          <legend className="mb-1.5 text-sm text-muted">Готовые услуги: добавляют строку, её можно поправить</legend>
          <div className="flex flex-wrap gap-2">
            {catalog.map((c) => (
              <button
                key={c.id}
                type="button"
                className="chip"
                title={c.kind === 'subscription' ? 'регулярные платежи, в месяц' : c.note}
                onClick={() => setLines((ls) => [...ls, blankLine({ title: c.title, description: c.note, price: minorToText(c.price.amountMinor), unit: c.kind === 'subscription' ? 'мес.' : 'усл.' })])}
              >
                + {c.title} · {formatMoney(c.price)}
              </button>
            ))}
          </div>
        </fieldset>
      )}
      <LinesEditor lines={lines} onChange={setLines} currency={currency} showProblems={shown} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Проект</span>
          <select className="field" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Без проекта</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Оплатить до</span>
          <input className="field" type="date" value={dueDate} min={isoInDays(0)} onChange={(e) => setDueDate(e.target.value)} required />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Основание</span>
          <input className="field" maxLength={200} placeholder="Договор № …, Запрос «…», Этап «…»" value={basis} onChange={(e) => setBasis(e.target.value)} />
        </label>
      </div>
      {payers.length > 1 &&
        (draft ? (
          <p className="text-sm text-muted">Плательщик: {draft.payer?.name ?? 'основной'}</p>
        ) : (
          <label className="block text-sm">
            <span className="mb-1.5 block text-muted">Плательщик</span>
            <select className="field" value={payerId} onChange={(e) => setPicked(e.target.value)} aria-label="Плательщик">
              {payers.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.legalName || 'Название не указано'}
                  {x.id === projectPayer ? (projectId ? ' — плательщик проекта' : ' — основной') : ''}
                  {x.missing.length ? ` (нет: ${x.missing.join(', ')})` : ''}
                </option>
              ))}
            </select>
          </label>
        ))}
      <label className="block text-sm">
        <span className="mb-1.5 block text-muted">Комментарий клиенту (виден в кабинете и в PDF счёта)</span>
        <textarea className="field min-h-16" maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} />
      </label>
      {error && (
        <p role="alert" className="text-sm text-overdue">
          {error.message}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
        <span className="num font-semibold">
          <span className="mr-2 text-sm font-normal text-muted">Итого</span>
          {formatMoney(total)}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-secondary" disabled={pending} onClick={() => submit(false)}>
            Сохранить черновик
          </button>
          <button className="btn-primary" disabled={pending}>
            Выставить и отправить клиенту
          </button>
        </div>
      </div>
    </form>
  );
}
